import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Lock, Globe } from "lucide-react";
import { Switch } from "@/components/ui/switch";

export const AccessCard = () => {
  const [privateMode, setPrivateMode] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/access");
      setPrivateMode(!!data.private_mode);
    } catch {
      toast.error("Could not load access settings");
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (next) => {
    setSaving(true);
    setPrivateMode(next);
    try {
      const { data } = await api.put("/admin/access", { private_mode: next });
      setPrivateMode(!!data.private_mode);
      toast.success(next
        ? "Site is now private — new sign-ups are closed and only you can log in"
        : "Site is now public — anyone can sign up and log in");
    } catch (err) {
      setPrivateMode(!next);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update access");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-border bg-[#0A0A0A] rounded-sm p-6 space-y-4" data-testid="access-card">
      <div className="flex items-center gap-2">
        {privateMode ? <Lock className="w-4 h-4 text-[#FFCC00]" /> : <Globe className="w-4 h-4 text-[#34C759]" />}
        <h2 className="font-heading text-lg font-black tracking-tight">Site access</h2>
      </div>
      <p className="text-xs text-muted-foreground">
        Turn on <span className="text-white font-semibold">Private mode</span> to hide the platform from
        the public while you finish setting things up. The landing page still shows, but the
        "Get started / Create account" buttons are removed, new sign-ups (email and Google) are blocked,
        and only you (admin) can log in. Existing non-admin users can't sign in until you turn it back off.
      </p>
      <div className="flex items-center justify-between border border-border bg-background rounded-sm px-4 py-3">
        <div>
          <p className="text-sm font-semibold" data-testid="access-status-label">
            {loaded ? (privateMode ? "Private — invitations closed" : "Public — open to everyone") : "Loading…"}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {privateMode ? "Only admin logins are allowed right now." : "New visitors can create an account."}
          </p>
        </div>
        <Switch checked={privateMode} disabled={!loaded || saving} onCheckedChange={toggle}
          data-testid="access-private-toggle" />
      </div>
    </div>
  );
};
